(ns common.schema.entities.company.schema
  (:require
   [common.currencies :as currencies]
   [common.schema.constraints :as constraints]
   [common.schema.option-filters :as option-filters]
   [common.schema.entities.asset :as asset]
   [common.schema.entities.common :as common-schema]
   [common.schema.entities.customer :as customer]
   [common.country :as country]
   [common.tol-a :as tol-a]
   [common.tol-b :as tol-b]
   [common.schema.schema-utils :as su]
   [common.languages :as languages]))

(def import-fixed-fields
  [:common/external-id
   :asset/name
   :company/website])

(def schema
  (vec
   (concat
    asset/schema
    customer/schema

    (su/text-simple-field :company/business-id-fin
                          "Business ID (Fin)"
                          {:authorization su/access-ssg-ssg
                           :default-field-visibility :not-in-use
                           :constraints   {:constraint/valid-string-value
                                           {:validation-fn constraints/validate-finnish-business-id}}})

    (su/currency-field :company/cur-tesi-overdue-ap-and-other-payables
                       "Overdue AP and other payables"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use-blocked})
    (su/option-field :company/tesi-main-industry-usual-suspects
                     "Industry"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use-blocked})
    (su/option-field-multi :company/tag-therapeutic-target
                           "Therapeutic target"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use})
    (su/option-field-multi :company/tag-species
                           "Species"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use-blocked})
    (su/option-field-multi :company/tag-level-of-disruption
                           "Level of disruption"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use})
    (su/option-field-multi :company/tag-storage-methods
                           "Storage methods"
                           {:authorization su/access-ssg-ssg})
    (su/option-field-multi :company/tag-product-source
                           "Product source"
                           {:authorization su/access-ssg-ssg})
    (su/option-field-multi :company/tesi-pitchbook-verticals
                           "Industry specification"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use-blocked})
    (su/currency-field :company/cur-tesi-capital-raised-to-date
                       "Equity raised to date"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use-blocked})
    (su/integer-field :company/tesi-latest-capital-injection
                      "Year of latest equity round"
                      {:authorization su/access-ssg-ssg
                       :default-field-visibility :not-in-use-blocked})
    (su/currency-field :company/cur-tesi-post-money-valuation-previous-round
                       "Post-money valuation immediately after previous equity round"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use-blocked})
    (su/text-multiline-field :company/tesi-capital-raised-to-date-investors
                             "Previous equity round investors"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use-blocked})
    (su/text-multiline-field :company/target-markets
                             "Target markets"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use-blocked})
    (su/text-multiline-field :company/founders
                             "Founders"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    ;; Dead: `:company/current-owners` is defined again further down ("Current owners"), and the
    ;; registry keeps only that last definition. Deleting this one is not safe: the position of a
    ;; field in this vector is its default order, and the shift moves 132 company fields in the
    ;; default order. See `unique-attributes-test`.
    (su/text-multiline-field :company/current-owners
                             "Ownership overview"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use-blocked})
    (su/keyword-multi-field :company/languages
                            "Languages"
                            languages/options
                            {:authorization su/access-ssg-ssg})
    (su/integer-field :company/tesi-company-view-on-runway-in-months-with-additional-funding
                      "Expected runway extension with the proposed funding round (months)"
                      {:authorization su/access-ssg-ssg
                       :default-field-visibility :not-in-use-blocked})
    (su/text-multiline-field :company/future-funding-and-exit-plans
                             "Future funding and exit plans"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use-blocked})
    (su/text-multiline-field :company/tesi-key-performance-indicators
                             "Other relevant metrics about the business model"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use-blocked})
    (su/text-multiline-field :company/tesi-revenue-details-geography
                             "Revenue split per geographical area"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use-blocked})
    (su/currency-field :company/cur-tesi-balance-sheet-size
                       "Balance sheet, total"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use-blocked})
    (su/text-multiline-field :company/tesi-ESG-goals
                             "ESG goals"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use-blocked})
    (su/currency-field :company/cur-proposed-equity-investment-from-lead-investor
                       "Proposed equity from lead investor"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use-blocked})
    (su/text-multiline-field :company/tesi-non-equity-financiers
                             "Non equity financiers participating"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use-blocked})
    (su/integer-field :company/alexa-rank
                      "Alexa rank"
                      {:authorization su/access-ssg-ssg
                       :default-field-visibility :not-in-use})

    (su/option-field-multi :company/technology-maturity
                           "Technology maturity"
                           {:authorization su/access-ssg-ssg})
    (su/currency-field :company/cur-ASVDA-safe
                       "SAFE"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use-blocked})

    (su/text-simple-field :company/stock-symbol
                          "Stock symbol"
                          {:authorization su/access-ssg-ssg
                           :default-field-visibility :not-in-use})
    (su/text-simple-field :company/number-of-employees-info
                          "Number of employees estimate"
                          {:authorization su/access-ssg-ssg})

    (su/currency-field :company/cur-tesi-ebitda-2021
                       "EBITDA 2021"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use-blocked})
    (su/currency-field :company/cur-tesi-revenue-2021
                       "Revenue 2021"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use-blocked})

    [common-schema/common-network-drive-field]
    common-schema/common-network-drives-field
    [common-schema/common-domain-field]
    common-schema/common-folders-path

    [[:company/cur-company-registered-capital--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Company registered capital (currency)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-company-registered-capital
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-revenue-le-for-current-year
      {:field/data-type             :data.type/currency-value
       :field/default-ord           280
       :field/secondary-attribute   :company/cur-revenue-le-for-current-year--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Revenue (LE for current year)"}}]
     [:company/product-service
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           180
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Product / Service"}}]

     [:company/news
      {:field/data-type          :data.type/text-url ;; TODO: list of urls improvement :company/news-articles #{urls with metadata}
       :field/default-ord        270
       :field/default-visibility {:editor :editor.type/url
                                  :grid   :editor.type/url
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "News"}}]
     [:company/cur-debt-balance-last-known--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Debt balance (last known) (currency)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-debt-balance-last-known
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/ipr
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        670
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "IPR"}}]

     [:company/cur-revenue-ltm
      {:field/data-type             :data.type/currency-value
       :field/default-ord           300
       :field/secondary-attribute   :company/cur-revenue-ltm--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Revenue (LTM)"}}]
     [:company/pfc-status
      {:field/data-type    :data.type/keyword
       :field/default-ord  1210
       :value-type         :keyword
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/none
                                  :filter :filter.type/none
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :options            [{:key   :in-portfolio
                             :label "In portfolio"}]
       :field/translations {:en "Portfolio company status"}}]
     [:company/cur-proposed-pre-money-valuation
      {:field/data-type             :data.type/currency-value
       :field/default-ord           1220
       :field/secondary-attribute   :company/cur-proposed-pre-money-valuation--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Proposed pre-money valuation"}}]
     [:company/number-of-customers
      {:field/data-type             :data.type/integer
       :field/default-ord           220
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/integer
                                     :grid   :editor.type/integer
                                     :filter :filter.type/integer
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Number of customers"}}]
     [:company/cur-serviceable-available-market--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "SAM (currency)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-serviceable-available-market
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/parent-company
      {:field/data-type          :data.type/text-simple
       :field/default-ord        740
       :field/default-visibility {:editor :editor.type/text-simple
                                  :grid   :editor.type/text-simple
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Parent company"}}]
     [:company/cur-cash-balance-last-known--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Cash balance (last known) (currency)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-cash-balance-last-known
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/crunchbase
      {:field/data-type          :data.type/text-url
       :field/default-ord        120
       :field/default-visibility {:editor :editor.type/url
                                  :grid   :editor.type/url
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Crunchbase"}}]
     [:company/cur-revenue-last-calendar-year
      {:field/data-type             :data.type/currency-value
       :field/default-ord           320
       :field/secondary-attribute   :company/cur-revenue-last-calendar-year--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Revenue (last calendar year)"}}]
     [:company/revenue-le-for-current-year-comment
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        290
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Revenue (LE for current year) comment"}}]
     [:company/cur-clv
      {:field/data-type             :data.type/currency-value
       :field/default-ord           620
       :field/secondary-attribute   :company/cur-clv--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "CLV"}}]
     [:company/impact-of-costs-cuts
      {:field/data-type             :data.type/text-multiline
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-ord           420
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Impact of cost cuts"}}]
     [:company/tesi-corona-impact-on-business
      {:field/data-type          :data.type/text-multiline
       :field/constraints        {:constraint/string-max-length 2000}
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/text-simple
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Corona impact on revenue"}}]
     [:company/current-owners
      {:field/data-type          :data.type/text-multiline
       :field/constraints        {:constraint/string-max-length 2000}
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/text-simple
                                  :filter :filter.type/text
                                  :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Current owners"}}]
     [:company/cur-tesi-net-debt
      {:field/data-type             :data.type/currency-value
       :field/secondary-attribute   :company/cur-interest-bearing-debt-at-end-of-last-year--sym
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/composite-attributes  [:company/cur-cash-and-bank-receivables-at-end-of-last-year
                                     :company/cur-interest-bearing-debt-at-end-of-last-year]
       :field/calculated-field
       (fn [{:keys [logger entity-data entity-lookup tr bigdec->float get-field-schema
                    display-string]}]
         (let [f-cash (bigdec->float (:company/cur-cash-and-bank-receivables-at-end-of-last-year
                                      entity-data))
               f-debt (bigdec->float (:company/cur-interest-bearing-debt-at-end-of-last-year
                                      entity-data))]
           (cond
             (not (number? f-cash))
             (tr :not-a-number
                 [(tr :company/cur-cash-and-bank-receivables-at-end-of-last-year)])
             (not (number? f-debt))
             (tr :not-a-number [(tr :company/cur-interest-bearing-debt-at-end-of-last-year)])
             (not (= (:company/cur-cash-and-bank-receivables-at-end-of-last-year--sym entity-data)
                     (:company/cur-interest-bearing-debt-at-end-of-last-year--sym entity-data)))
             (tr :not-same-currency
                 [(str (tr :company/cur-interest-bearing-debt-at-end-of-last-year)
                       ", "
                       (tr :company/cur-cash-and-bank-receivables-at-end-of-last-year))])
             :else
             (- f-debt f-cash))))
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/system}
       :field/translations          {:en "Net debt"}}]
     [:company/tesi-proposed-non-govt-funding-percentage
      {:field/data-type             :data.type/percentage
       :field/default-visibility    {:editor :editor.type/percentage
                                     :grid   :editor.type/percentage
                                     :filter :filter.type/percentage
                                     :field-visibility :not-in-use-blocked}
       :field/composite-attributes  [:company/cur-proposed-funding-from-non-govt-sources
                                     :company/cur-proposed-equity-round-size]
       :field/calculated-field
       (fn [{:keys [logger entity-data entity-lookup tr bigdec->float get-field-schema
                    display-string]}]
         (let [f-non-govt (bigdec->float (:company/cur-proposed-funding-from-non-govt-sources
                                          entity-data))
               f-size (bigdec->float (:company/cur-proposed-equity-round-size entity-data))]
           (cond
             (not (number? f-non-govt))
             (tr :not-a-number [(tr :company/cur-proposed-funding-from-non-govt-sources)])
             (not (number? f-size))
             (tr :not-a-number [(tr :company/cur-proposed-equity-round-size)])
             (not (= (:company/cur-proposed-funding-from-non-govt-sources--sym entity-data)
                     (:company/cur-proposed-equity-round-size--sym entity-data)))
             (tr :not-same-currency
                 [(str (tr :company/cur-proposed-funding-from-non-govt-sources)
                       ", "
                       (tr :company/cur-proposed-equity-round-size))])
             :else
             (/ f-non-govt f-size))))
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/system}
       :field/translations          {:en "Proposed non-govt funding-%"}}]
     [:company/cur-tesi-net-debt-per-ebitda
      {:field/data-type             :data.type/currency-value
       :field/secondary-attribute   :company/cur-tesi-net-debt-per-ebitda
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/composite-attributes  [:company/cur-cash-and-bank-receivables-at-end-of-last-year
                                     :company/cur-interest-bearing-debt-at-end-of-last-year
                                     :company/cur-tesi-ebitda-2019]
       :field/calculated-field
       (fn [{:keys [logger entity-data entity-lookup tr bigdec->float get-field-schema
                    display-string]}]
         (let [f-cash (bigdec->float (:company/cur-cash-and-bank-receivables-at-end-of-last-year
                                      entity-data))
               f-debt (bigdec->float (:company/cur-interest-bearing-debt-at-end-of-last-year
                                      entity-data))
               f-ebitda (bigdec->float (:company/cur-tesi-ebitda-2019 entity-data))]
           (cond
             (not (number? f-cash))
             (tr :not-a-number
                 [(tr :company/cur-cash-and-bank-receivables-at-end-of-last-year)])
             (not (number? f-debt))
             (tr :not-a-number [(tr :company/cur-interest-bearing-debt-at-end-of-last-year)])
             (not (number? f-ebitda))
             (tr :not-a-number [(tr :company/cur-tesi-ebitda-2019)])
             (not (= (:company/cur-cash-and-bank-receivables-at-end-of-last-year--sym entity-data)
                     (:company/cur-interest-bearing-debt-at-end-of-last-year--sym entity-data)
                     (:company/cur-tesi-ebitda-2019--sym entity-data)))
             (tr :not-same-currency
                 [(str (tr :company/cur-interest-bearing-debt-at-end-of-last-year)
                       ", "
                       (tr :company/cur-cash-and-bank-receivables-at-end-of-last-year)
                       ", "
                       (tr :company/cur-tesi-ebitda-2019))])
             :else
             (let [dividend (- f-debt f-cash)
                   divider f-ebitda]
               (if (zero? divider)
                 (if (> dividend 0)
                   "+∞"
                   "-∞")
                 (/ dividend divider))))))
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/system}
       :field/translations          {:en "Net debt / EBITDA"}}]
     [:company/cur-tesi-net-debt-per-ebitda--sym
      {:field/data-type :data.type/currency-symbol
       :field/secondary-attribute :company/cur-tesi-net-debt-per-ebitda
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-cash-balance-last-known
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword
       :field/translations {:en "Net debt per EBITDA (symbol)"}}]
     [:company/tesi-identified-corona-mitigation-without-funding
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           420
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Identified mitigation without funding"}}]
     [:company/cur-short-term-receivables
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-short-term-receivables--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Short term receivables"}}]
     [:company/cur-cash-and-bank-receivables-at-end-of-last-year
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-cash-and-bank-receivables-at-end-of-last-year--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Cash and bank receivables at end of last year"}}]
     [:company/cur-interest-bearing-debt-at-end-of-last-year
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-interest-bearing-debt-at-end-of-last-year--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Interest bearing debt at end of last year"}}]
     [:company/cur-short-term-debt
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-short-term-debt--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Short term debt"}}]
     [:company/cur-short-term-advances
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-short-term-advances--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Short term advance payments"}}]
     [:company/cur-tesi-net-working-capital
      {:field/data-type             :data.type/currency-value
       :field/secondary-attribute   :company/cur-accounts-payable--sym
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/composite-attributes  [:company/cur-accounts-receivable
                                     :company/cur-accounts-payable
                                     :company/cur-current-assets]
       :field/calculated-field      (fn [{:keys [logger entity-data entity-lookup tr bigdec->float
                                                 get-field-schema display-string]}]
                                      (let [f-receivables (bigdec->float
                                                           (:company/cur-accounts-receivable
                                                            entity-data))
                                            f-payable (bigdec->float (:company/cur-accounts-payable
                                                                      entity-data))
                                            f-assets (bigdec->float (:company/cur-current-assets
                                                                     entity-data))]
                                        (cond
                                          (not (number? f-receivables))
                                          (tr :not-a-number
                                              [(tr :company/cur-accounts-receivable)])
                                          (not (number? f-payable))
                                          (tr :not-a-number [(tr :company/cur-accounts-payable)])
                                          (not (number? f-assets))
                                          (tr :not-a-number [(tr :company/cur-current-assets)])
                                          (not
                                           (= (:company/cur-accounts-receivable--sym entity-data)
                                              (:company/cur-accounts-payable--sym entity-data)
                                              (:company/cur-accounts-payable--sym entity-data)))
                                          (tr :not-same-currency
                                              [(str (tr :company/cur-accounts-receivable)
                                                    ", "
                                                    (tr :company/cur-accounts-payable)
                                                    ", "
                                                    (tr :company/cur-current-assets))])
                                          :else
                                          (- (+ f-assets f-receivables)
                                             f-payable))))
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/system}
       :field/translations          {:en "Net working capital"}}]
     [:company/cur-necessary-investments-this-year
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-necessary-investments-this-year--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Necessary investments this year"}}]
     [:company/cur-long-term-advances
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-long-term-advances--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Long term advance payments"}}]
     [:company/cur-fixed-costs-last-year
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-fixed-costs-last-year--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Fixed costs per anno"}}]
     [:company/cur-fixed-costs-current-last-month
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-fixed-costs-current-last-month--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Fixed costs last month"}}]
     [:company/cur-equity
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-equity--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Equity"}}]
     [:company/cur-current-cash-position-inc-bank-receivables
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-current-cash-position-inc-bank-receivables--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Cash & bank receivables"}}]
     [:company/cur-current-assets
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-current-assets--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Current assets"}}]
     [:company/cur-proposed-funding-from-us
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-proposed-funding-from-us--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Proposed funding from us"}}]
     [:company/cur-proposed-funding-from-non-govt-sources
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-proposed-funding-from-non-govt-sources--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Proposed non-govt funding"}}]
     [:company/cur-balance-sheet-size
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-balance-sheet-size--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Balance sheet size"}}]
     [:company/cur-accounts-receivable
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-accounts-receivable--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Accounts receivable"}}]
     [:company/cur-accounts-payable
      {:field/data-type           :data.type/currency-value
       :field/secondary-attribute :company/cur-accounts-payable--sym
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Accounts payable"}}]
     [:company/cur-cash-balance-last-month
      {:field/data-type             :data.type/currency-value
       :field/default-ord           690
       :field/secondary-attribute   :company/cur-cash-balance-last-month--sym
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Cash balance (last month)"}}]
     [:company/cur-opex-le-for-current-year--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "OPEX (LE for current year) currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-opex-le-for-current-year
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/linkedin
      {:field/data-type          :data.type/text-url
       :field/default-ord        110
       :field/default-visibility {:editor :editor.type/url
                                  :grid   :editor.type/url
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Company LinkedIn profile"}}]
     [:company/xing
      {:field/data-type          :data.type/text-url
       :field/default-visibility {:editor :editor.type/url
                                  :grid   :editor.type/url
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Company Xing profile"}}]
     [:company/instagram
      {:field/data-type          :data.type/text-url
       :field/default-visibility {:editor :editor.type/url
                                  :grid   :editor.type/url
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Company Instagram profile"}}]
     [:company/cur-proposed-equity-round-size
      {:field/data-type             :data.type/currency-value
       :field/default-ord           1230
       :field/secondary-attribute   :company/cur-proposed-equity-round-size--sym
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Proposed equity round size"}}]
     [:company/irl
      {:field/data-type             :data.type/integer
       :field/default-ord           430
       :field/constraints           {:constraint/greater-than-inclusive 1
                                     :constraint/smaller-than-inclusive 9}
       :field/default-visibility    {:editor :editor.type/integer
                                     :grid   :editor.type/integer
                                     :filter :filter.type/integer
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "IRL"}}]
     [:company/cur-ebitda-last-calendar-year--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "EBITDA (last calendar year) currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-ebitda-last-calendar-year
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/ebitda-last-calendar-year-comment
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        420
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "EBITDA (last calendar year) comment"}}]
     [:company/indication
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        660
       :field/default-visibility {:field-visibility :not-in-use-blocked
                                  :editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Indication"}}]
     [:company/background
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        470
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Company background"}}]
     [:company/customer-identification-number
      {:field/data-type          :data.type/text-simple
       :field/default-ord        350
       :field/default-visibility {:field-visibility :not-in-use
                                  :editor :editor.type/text-simple
                                  :grid   :editor.type/text-simple
                                  :filter :filter.type/text}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "LLPIN/CIN/Form INC-1 Ref No"}}]
     [:company/company-business-type
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        790
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Company business type"}}]
     [:company/cur-previous-round-valuation
      {:field/data-type             :data.type/currency-value
       :field/default-ord           810
       :field/secondary-attribute   :company/cur-previous-round-valuation--sym
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Valuation previous round (pre-money)"}}]
     [:company/cur-serviceable-obtainable-market--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "SOM (currency)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-serviceable-obtainable-market
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-ebitda-le-for-current-year--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "EBITDA (LE for current year) currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-ebitda-le-for-current-year
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/youtube
      {:field/data-type          :data.type/text-url
       :field/default-ord        650
       :field/default-visibility {:editor :editor.type/url
                                  :grid   :editor.type/url
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Youtube"}}]
     [:company/revenue-last-calendar-year-comment
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        330
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Revenue (last calendar year) comment"}}]
     [:company/financials
      {:field/data-type             :data.type/financials
       :field/default-visibility    {:editor :editor.type/financials
                                     :grid   :editor.type/none
                                     :filter :filter.type/none
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/not-importable?       true
       :field/translations          {:en "Financials"}}]
     [:company/tesi-financials
      {:field/data-type             :data.type/tesi-financials
       :field/default-visibility    {:editor :editor.type/tesi-financials
                                     :grid   :editor.type/none
                                     :filter :filter.type/none
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/not-importable?       true
       :field/translations          {:en "Custom financials"}}]
     [:company/tesi-financials-2
      {:field/data-type             :data.type/tesi-financials-2
       :field/default-visibility    {:editor :editor.type/tesi-financials-2
                                     :grid   :editor.type/none
                                     :filter :filter.type/none
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/not-importable?       true
       :field/translations          {:en "Custom financials 2"}}]

     [:company/number-of-employees
      {:field/data-type             :data.type/integer
       :field/default-ord           2302
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/integer
                                     :grid   :editor.type/integer
                                     :filter :filter.type/integer
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Number of employees"}}]
     [:company/number-of-employees-in-Finland
      {:field/data-type             :data.type/integer
       :field/default-ord           230
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/integer
                                     :grid   :editor.type/integer
                                     :filter :filter.type/integer
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Number of employees in Finland"}}]
     [:company/current-owner
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        340
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Current owner(s)"}}]
     [:company/cur-gross-profits-le-for-current-year--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Gross profits (LE for current year) currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-gross-profits-le-for-current-year
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-capital-raised
      {:field/data-type             :data.type/currency-value
       :field/default-ord           440
       :field/secondary-attribute   :company/cur-capital-raised--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Capital raised"}}]
     [:company/received-grants
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        780
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Received grants"}}]
     [:company/cur-burn-rate
      {:field/data-type             :data.type/currency-value
       :field/default-ord           630
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-burn-rate--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Burn rate (monthly)"}}]
     [:company/cur-revenue-last-known
      {:field/data-type           :data.type/currency-value
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute :company/cur-revenue-last-known--sym
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Revenue (Last known)"}}]
     [:company/cur-gross-profit-last-known
      {:field/data-type           :data.type/currency-value
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute :company/cur-gross-profit-last-known--sym
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Gross profit currency"}}]
     [:company/cur-ebit-last-known
      {:field/data-type           :data.type/currency-value
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute :company/cur-ebit-last-known--sym
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "EBIT (Last known)"}}]
     [:company/cur-ebitda-last-known
      {:field/data-type           :data.type/currency-value
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute :company/cur-ebitda-last-known--sym
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "EBITDA (Last known)"}}]
     [:company/cur-previous-funding-round
      {:field/data-type           :data.type/currency-value
       :field/constraints         {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute :company/cur-previous-funding-round--sym
       :field/default-visibility  {:editor :editor.type/number
                                   :grid   :editor.type/number
                                   :filter :filter.type/number
                                   :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations        {:en "Previous funding round"}}]
     [:company/tesi-export-percentage-of-revenue
      {:field/data-type          :data.type/percentage
       :field/default-visibility {:editor :editor.type/percentage
                                  :grid   :editor.type/percentage
                                  :filter :filter.type/percentage
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Revenue from exports"}}]
     [:company/tesi-corona-percentage-impact-on-2020-revenue
      {:field/data-type          :data.type/percentage
       :field/default-visibility {:editor :editor.type/percentage
                                  :grid   :editor.type/percentage
                                  :filter :filter.type/percentage
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Corona impact on revenue"}}]
     [:company/tesi-corona-percentage-impact-on-2021-revenue
      {:field/data-type          :data.type/percentage
       :field/default-visibility {:editor :editor.type/percentage
                                  :grid   :editor.type/percentage
                                  :filter :filter.type/percentage
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Corona impact on revenue 2021"}}]
     [:company/tesi-corona-percentage-impact-on-2020-ebitda
      {:field/data-type          :data.type/percentage
       :field/default-visibility {:editor :editor.type/percentage
                                  :grid   :editor.type/percentage
                                  :filter :filter.type/percentage
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Corona impact on ebitda"}}]
     [:company/tesi-corona-percentage-impact-on-2021-ebitda
      {:field/data-type          :data.type/percentage
       :field/default-visibility {:editor :editor.type/percentage
                                  :grid   :editor.type/percentage
                                  :filter :filter.type/percentage
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Corona impact on ebitda 2021"}}]
     [:company/last-known-6m-growth-rate
      {:field/data-type          :data.type/percentage
       :field/default-visibility {:editor :editor.type/percentage
                                  :grid   :editor.type/percentage
                                  :filter :filter.type/percentage
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Last known 6m growth rate (%)"}}]
     [:company/last-known-12m-growth-rate
      {:field/data-type          :data.type/percentage
       :field/default-visibility {:editor :editor.type/percentage
                                  :grid   :editor.type/percentage
                                  :filter :filter.type/percentage
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Last known 12m growth rate (%)"}}]
     [:company/last-known-24m-growth-rate
      {:field/data-type          :data.type/percentage
       :field/default-visibility {:editor :editor.type/percentage
                                  :grid   :editor.type/percentage
                                  :filter :filter.type/percentage
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Last known 24m growth rate (%)"}}]
     [:company/date-of-previous-funding-round
      {:field/data-type          :data.type/datetime
       :field/default-visibility {:editor :editor.type/date
                                  :grid   :editor.type/date
                                  :filter :filter.type/date
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Date of previous funding round"}}]
     [:company/subsidiaries
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        640
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Subsidiaries"}}]
     [:company/facebook
      {:field/data-type          :data.type/text-url
       :field/default-ord        240
       :field/default-visibility {:editor :editor.type/url
                                  :grid   :editor.type/url
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Facebook"}}]
     [:company/cur-mrr--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "MRR currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-mrr
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-ebitda-ltm--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "EBITDA (LTM) currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-ebitda-ltm
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-total-available-market
      {:field/data-type             :data.type/currency-value
       :field/default-ord           500
       :field/secondary-attribute   :company/cur-total-available-market--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "TAM"}
      }]
     [:company/product-development-status
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        450
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Status of product development"}
      }]
     [:company/cur-mrr
      {:field/data-type             :data.type/currency-value
       :field/default-ord           600
       :field/secondary-attribute   :company/cur-mrr--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "MRR"}
      }]
     [:company/cur-previous-round-valuation--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Valuation previous round (currency)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-previous-round-valuation
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/customer-segments
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           170
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Customer segments"}
      }]
     [:company/recent-status
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        1230
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations       {:en "Recent status"}
      }]
     [:company/tracxn
      {:field/data-type          :data.type/text-url
       :field/default-ord        480
       :field/default-visibility {:editor :editor.type/url
                                  :grid   :editor.type/url
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Tracxn"}
      }]
     [:company/twitter
      {:field/data-type          :data.type/text-url
       :field/default-ord        245
       :field/default-visibility {:editor :editor.type/url
                                  :grid   :editor.type/url
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Twitter"}
      }]
     [:company/pitchbook
      {:field/data-type          :data.type/text-url
       :field/default-ord        245
       :field/default-visibility {:editor :editor.type/url
                                  :grid   :editor.type/url
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "PitchBook url"}
      }]
     [:company/cur-proposed-pre-money-valuation--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Proposed pre-money valuation currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-proposed-pre-money-valuation
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/known-investors
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        250
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Known investors"}
      }]
     [:company/lead-investors
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        255
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Lead investor(s)"}
      }]
     [:company/sustainable-development-goals
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        730
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Sustainable development goals"}
      }]
     [:company/cur-gross-profits-le-for-current-year
      {:field/data-type             :data.type/currency-value
       :field/default-ord           580
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-gross-profits-le-for-current-year--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Gross profits (LE for current year)"}
      }]
     [:company/exit-plan
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        1240
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-buy-side
                                     :access/write :role/guest-buy-side}
       :field/translations       {:en "Exit plan"}
      }]
     [:company/past-accelerators
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        760
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Past accelerators"}
      }]
     [:company/cur-burn-rate--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Burn rate currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-burn-rate
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-revenue-last-known--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Revenue (Last known) (currency)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-revenue-last-known
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :in-use}
       :value-type :keyword}]
     [:company/cur-gross-profit-last-known--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Gross profit currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-gross-profit-last-known
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-ebit-last-known--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "EBIT (Last known) currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-ebit-last-known
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-ebitda-last-known--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "EBITDA currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-ebitda-last-known
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :in-use}
       :value-type :keyword}]
     [:company/cur-previous-funding-round--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Previous funding round currency"}
       :field/constraints {}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-previous-funding-round
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/overview
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        50
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Company overview"}
      }]
     ;; TODO: migration remove - use :asset/country instead

     [:company/country
      {:field/data-type             :data.type/text-simple
       :field/default-ord           1250
       :field/not-importable?       true
       :field/default-visibility    {:editor :editor.type/none
                                     :grid   :editor.type/none
                                     :filter :filter.type/none
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/system
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :field/translations          {:en "Country"}}]
     [:company/logo
      {:field/data-type             :data.type/text-url
       :field/default-ord           1250
       :field/not-importable?       true
       :field/default-visibility    {:editor :editor.type/none
                                     :grid   :editor.type/none
                                     :filter :filter.type/none
                                     :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/system-semantics      su/system-controlled-optional-field-semantics
       :field/translations          {:en "Logo"}}]
     [:company/cur-clv--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "CLV currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-clv
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-tesi-net-debt--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/system}
       :field/translations {:en "Net debt"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-tesi-net-debt
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :value-type :keyword}]
     [:company/cur-short-term-receivables--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Short term receivables"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-short-term-receivables
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-cash-and-bank-receivables-at-end-of-last-year--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Cash and bank receivables at end of last year (symbol)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-cash-and-bank-receivables-at-end-of-last-year
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-interest-bearing-debt-at-end-of-last-year--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Interest bearing debt at end of last year (symbol)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-interest-bearing-debt-at-end-of-last-year
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]


     [:company/cur-short-term-debt--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Short term debt"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-short-term-debt
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-short-term-advances--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Short term advance payments"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-short-term-advances
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-tesi-net-working-capital--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/system}
       :field/translations {:en "Net working capital"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-tesi-net-working-capital
       :field/composite-attributes [:company/cur-accounts-receivable--sym
                                    :company/cur-accounts-payable--sym
                                    :company/cur-current-assets--sym]
       :field/calculated-field (fn [{:keys [logger entity-data entity-lookup tr bigdec->float
                                            get-field-schema display-string]}]
                                 (when (not (= (:company/cur-accounts-receivable--sym entity-data)
                                               (:company/cur-accounts-payable--sym entity-data)
                                               (:company/cur-accounts-payable--sym entity-data)))
                                   nil
                                   (:company/cur-accounts-payable--sym entity-data)))

       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :value-type :keyword}]
     [:company/cur-necessary-investments-this-year--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Necessary investments this year"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-necessary-investments-this-year
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-long-term-advances--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Long term advance payments"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-long-term-advances
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-fixed-costs-last-year--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Fixed costs per anno"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-fixed-costs-last-year
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-fixed-costs-current-last-month--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Fixed costs last month"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-fixed-costs-current-last-month
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-equity--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Equity"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-equity
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-current-cash-position-inc-bank-receivables--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Cash & bank receivables"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-current-cash-position-inc-bank-receivables
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-current-assets--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Current assets"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-current-assets
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-proposed-funding-from-us--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Proposed funding from us"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-proposed-funding-from-us
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-proposed-funding-from-non-govt-sources--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Proposed non-govt funding"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-proposed-funding-from-non-govt-sources
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-balance-sheet-size--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Balance sheet size"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-balance-sheet-size
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-accounts-receivable--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Accounts receivable"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-accounts-receivable
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-accounts-payable--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Accounts payable"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-accounts-payable
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-debt-balance-last-known
      {:field/data-type             :data.type/currency-value
       :field/default-ord           720
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-debt-balance-last-known--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Debt balance (last known)"}
      }]
     [:company/received-loans
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        830
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Received loans"}
      }]
     [:company/joint-ventures
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        750
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Joint ventures"}
      }]
     [:company/cur-costs-le-for-current-year--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Costs (LE for current year) currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-costs-le-for-current-year
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-total-available-market--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "TAM (currency)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-total-available-market
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/duns-number
      {:field/data-type          :data.type/text-simple
       :field/default-ord        100
       :field/default-visibility {:editor :editor.type/text-simple
                                  :grid   :editor.type/text-simple
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "DUNS-number"}
      }]
     [:company/outstanding-convertibles-options-and-warrants
      {:field/data-type          :data.type/text-simple
       :field/default-ord        540
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/text-simple
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Outstanding convertibles options and warrants"}
      }]
     [:company/cur-revenue-last-calendar-year--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Revenue (last calendar year) currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-revenue-last-calendar-year
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-ebitda-last-calendar-year
      {:field/data-type             :data.type/currency-value
       :field/default-ord           410
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-ebitda-last-calendar-year--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "EBITDA (last calendar year)"}
      }]

     [:company/cur-tesi-revenue-2017
      {:field/data-type             :data.type/currency-value
       :field/default-ord           410
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-tesi-revenue-2017--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Revenue 2017"}
      }]
     [:company/cur-tesi-revenue-2017--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Revenue 2017 currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-tesi-revenue-2017
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :value-type :keyword}]
     [:company/cur-tesi-revenue-2018
      {:field/data-type             :data.type/currency-value
       :field/default-ord           410
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-tesi-revenue-2018--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Revenue 2018"}
      }]
     [:company/cur-tesi-revenue-2018--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Revenue 2018 currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-tesi-revenue-2018
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :value-type :keyword}]
     [:company/cur-tesi-revenue-2019
      {:field/data-type             :data.type/currency-value
       :field/default-ord           410
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-tesi-revenue-2019--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Revenue 2019"}
      }]
     [:company/cur-tesi-revenue-2019--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Revenue 2019 currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-tesi-revenue-2019
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :value-type :keyword}]
     [:company/cur-tesi-revenue-2020
      {:field/data-type             :data.type/currency-value
       :field/default-ord           410
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-tesi-revenue-2020--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Revenue 2020"}
      }]
     [:company/cur-tesi-revenue-2020--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Revenue 2020 currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-tesi-revenue-2020
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :value-type :keyword}]
     [:company/cur-tesi-ebitda-2017
      {:field/data-type             :data.type/currency-value
       :field/default-ord           410
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-tesi-ebitda-2017--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "EBITDA 2017"}
      }]
     [:company/cur-tesi-ebitda-2017--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "EBITDA 2017 currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-tesi-ebitda-2017
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :value-type :keyword}]
     [:company/cur-tesi-ebitda-2018
      {:field/data-type             :data.type/currency-value
       :field/default-ord           410
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-tesi-ebitda-2018--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "EBITDA 2018"}
      }]
     [:company/cur-tesi-ebitda-2018--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "EBITDA 2018 currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-tesi-ebitda-2018
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :value-type :keyword}]
     [:company/cur-tesi-ebitda-2019
      {:field/data-type             :data.type/currency-value
       :field/default-ord           410
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-tesi-ebitda-2019--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "EBITDA 2019"}
      }]
     [:company/cur-tesi-ebitda-2019--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "EBITDA 2019 currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-tesi-ebitda-2019
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :value-type :keyword}]
     [:company/cur-tesi-ebitda-2020
      {:field/data-type             :data.type/currency-value
       :field/default-ord           410
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-tesi-ebitda-2020--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "EBITDA 2020"}
      }]
     [:company/cur-tesi-ebitda-2020--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "EBITDA 2020 currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-tesi-ebitda-2020
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :value-type :keyword}]


     [:company/ebitda-ltm-comment
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        400
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "EBITDA (LTM) comment"}
      }]
     [:company/customer-problems
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        150
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Customer problems"}
      }]
     [:company/past-incubators
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        820
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Past incubators"}
      }]
     [:company/pfc-responsible
      {:field/data-type          :data.type/ref-single
       :field/default-ord        1260
       :field/entity-types       [:entity.type/account]
       :field/constraints        {}
       :value-type               :entity
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/user}
       :field/translations       {:en "Responsible team member"}
       :field/option-filter-fn   option-filters/active-users
       :field/os-filter-terms-fn    (fn [{:keys [] :as args}]
                                      {"extras.active-user" true})
      }]
     [:company/intelligence-company
      {:field/data-type          :data.type/ref-single
       :field/default-ord        1260
       :field/constraints        {}
       :value-type               :entity
       :field/entity-types       [:entity.type/any]
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/none
                                  :filter :filter.type/none
                                  :field-visibility :not-in-use-blocked}
       :field/hidden-from-configuration? true
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/system-semantics   su/system-controlled-optional-field-semantics
       :field/translations       {:en "Intelligence data"}}]
     [:company/fullcontact-company
      {:field/data-type          :data.type/ref-single
       :value-type               :entity
       :field/entity-types       [:entity.type/any]
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/none
                                  :filter :filter.type/none
                                  :field-visibility :not-in-use-blocked}
       :field/hidden-from-configuration? true
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/serialization      :normal
       :field/system-semantics   su/system-controlled-optional-field-semantics
       :field/translations       {:en "Intelligence data"}}]
     [:company/clearbit-company
      {:field/data-type          :data.type/ref-single
       :value-type               :entity
       :field/entity-types       [:entity.type/any]
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/none
                                  :filter :filter.type/none
                                  :field-visibility :in-use-disabled}
       :field/hidden-from-configuration? true
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/system}
       :field/serialization      :normal
       :field/system-semantics   su/system-controlled-optional-field-semantics
       :field/translations       {:en "Clearbit intelligence data"}}]
     [:company/company-owner
      {:field/data-type          :data.type/ref-single
       :field/default-ord        30
       :field/not-clearable?     true
       :value-type               :entity
       :field/entity-types       [:entity.type/account]
       :field/constraints        {}
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/user}
       :field/translations       {:en "Company contact owner"}
       :field/option-filter-fn   option-filters/active-users
       :field/os-filter-terms-fn    (fn [{:keys [] :as args}]
                                      {"extras.active-user" true})}]
     [:company/established-in
      {:field/data-type             :data.type/integer
       :field/default-ord           20
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/integer
                                     :grid   :editor.type/integer
                                     :filter :filter.type/integer
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Established in year"}
      }]
     [:company/cur-cash-balance-last-month--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Cash balance (last month) (currency)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-cash-balance-last-month
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-ebitda-ltm
      {:field/data-type             :data.type/currency-value
       :field/default-ord           390
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-ebitda-ltm--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "EBITDA (LTM)"}
      }]
     [:company/cur-costs-le-for-current-year
      {:field/data-type             :data.type/currency-value
       :field/default-ord           570
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-costs-le-for-current-year--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Costs (LE for current year)"}
      }]
     [:company/cur-company-registered-capital
      {:field/data-type             :data.type/currency-value
       :field/default-ord           800
       :field/secondary-attribute   :company/cur-company-registered-capital--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Company registered capital"}
      }]
     [:company/angellist
      {:field/data-type          :data.type/text-url
       :field/default-ord        130
       :field/default-visibility {:editor :editor.type/url
                                  :grid   :editor.type/url
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "AngelList"}
      }]
     [:company/link-to-company-presentation
      {:field/data-type          :data.type/text-url
       :field/default-ord        495
       :field/default-visibility {:editor :editor.type/url
                                  :grid   :editor.type/url
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Link to company presentation"}
      }]
     [:company/cur-debt-balance-last-month
      {:field/data-type             :data.type/currency-value
       :field/default-ord           700
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-debt-balance-last-month--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Debt balance (last month)"}
      }]
     [:company/technology
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        210
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Technology"}
      }]
     [:company/team
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        260
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Team overview"}
      }]
     [:company/cur-proposed-equity-round-size--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Proposed equity round size currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-proposed-equity-round-size
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-serviceable-available-market
      {:field/data-type             :data.type/currency-value
       :field/default-ord           510
       :field/secondary-attribute   :company/cur-serviceable-available-market--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "SAM"}
      }]
     [:company/scaling-plans
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        550
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Scaling plans"}
      }]
     [:company/business-id
      {:field/data-type          :data.type/text-simple
       :field/default-ord        90
       :field/default-visibility {:editor :editor.type/text-simple
                                  :grid   :editor.type/text-simple
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Business ID"}
      }]
     [:company/cur-ebitda-le-for-current-year
      {:field/data-type             :data.type/currency-value
       :field/default-ord           370
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-ebitda-le-for-current-year--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "EBITDA (LE for current year)"}
      }]
     [:company/business-traction
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        530
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Business traction"}
      }]
     [:company/cur-cash-balance-last-known
      {:field/data-type             :data.type/currency-value
       :field/default-ord           710
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-cash-balance-last-known--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Cash balance (last known)"}
      }]
     [:company/competitive-advantages
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        680
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Competitive advantages"}
      }]
     [:company/ebitda-le-for-current-year-comment
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        380
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "EBITDA (LE for current year) comment"}
      }]
     [:company/previous-rounds-description
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        560
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Previous round(s) description"}
      }]
     [:company/cur-cac
      {:field/data-type             :data.type/currency-value
       :field/default-ord           610
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-cac--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "CAC"}
      }]
     [:company/received-awards
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        770
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Received awards"}
      }]
     [:company/revenue-ltm-comment
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        310
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Revenue (LTM) comment"}
      }]
     [:company/cur-capital-raised--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Capital raised (currency)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-capital-raised
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/website
      {:field/data-type          :data.type/text-url
       :field/default-ord        140
       :field/default-visibility {:editor :editor.type/url
                                  :grid   :editor.type/url
                                  :filter :filter.type/text
                                  :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Company website"}
      }]

     [:company/hometown
      {:field/data-type             :data.type/text-simple
       :field/default-ord           80
       :field/constraints           {:constraint/string-min-length 2
                                     :constraint/string-max-length 100}
       :field/default-visibility    {:editor :editor.type/text-simple
                                     :grid   :editor.type/text-simple
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Home town"}
      }]
     [:company/HQ-zip-code
      {:field/data-type             :data.type/text-simple
       :field/default-ord           80
       :field/constraints           {:constraint/string-min-length 2
                                     :constraint/string-max-length 20}
       :field/default-visibility    {:editor :editor.type/text-simple
                                     :grid   :editor.type/text-simple
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "HQ zip code"}
      }]
     [:company/phone
      {:field/data-type             :data.type/text-simple
       :field/default-ord           80
       :field/constraints           {:constraint/string-max-length 160}
       :field/default-visibility    {:editor :editor.type/text-simple
                                     :grid   :editor.type/text-simple
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Phone"}
      }]
     [:company/cur-revenue-le-for-current-year--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Revenue (LE for current year) currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-revenue-le-for-current-year
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-opex-le-for-current-year
      {:field/data-type             :data.type/currency-value
       :field/default-ord           590
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/secondary-attribute   :company/cur-opex-le-for-current-year--sym
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "OPEX (LE for current year)"}
      }]
     [:company/competition
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Competition"}
      }]
     [:company/weaknesses
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Weaknesses"}
      }]
     [:company/threats
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Threats"}
      }]
     [:company/tesi-revenue-details
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-min-length 5
                                     :constraint/string-max-length 500}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Revenue details"}
      }]
     [:company/tesi-main-expenses
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Main expenses"}
      }]
     [:company/tesi-description-of-corona-mitigation
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Corona mitigation activities"}
      }]
     [:company/short-description-of-progress-during-last-12-months
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Progress in last 12 months"}
      }]
     [:company/short-description-of-plans-for-next-12-months
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Plans for next 12 months"}
      }]
     [:company/short-description-of-key-stakeholders
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Key stakeholder"}
      }]
     [:company/proposed-use-of-proceeds
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Proposed use of proceeds"}
      }]
     [:company/opportunities
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Opportunities"}
      }]
     [:company/market-growth
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Market growth"}
      }]
     [:company/earnings-logic
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Earnings logic"}
      }]
     [:company/description-of-ongoing-investor-talks
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Ongoing investor talks"}
      }]
     [:company/current-financiers
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Current financiers"}
      }]
     [:company/cost-structure
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           200
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Cost structure"}
      }]
     [:company/cur-serviceable-obtainable-market
      {:field/data-type             :data.type/currency-value
       :field/default-ord           520
       :field/secondary-attribute   :company/cur-serviceable-obtainable-market--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "SOM"}
      }]
     [:company/target-market
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           160
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Target market"}
      }]
     [:company/financials-comment
      {:field/data-type          :data.type/text-multiline
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Comment on financials"}
      }]
     [:company/business-model
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        190
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Business model"}
      }]
     [:company/cur-debt-balance-last-month--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Debt balance (last month) (currency)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-debt-balance-last-month
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-revenue-ltm--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Revenue (LTM) currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-revenue-ltm
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/cur-cac--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "CAC currency"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-cac
       :options currencies/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type :keyword}]
     [:company/tag-tags
      {:field/data-type          :data.type/ref-multi
       :field/default-ord        460
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-tags
       :field/default-visibility {:editor :editor.type/multi-select
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :in-use}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Company tags"}
      }]

     [:company/other-locations
      {:field/data-type    :data.type/keyword-multi
       :field/default-ord  1190
       :value-type         :keyword
       :field/default-visibility {:editor :editor.type/multi-select
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :options            country/options
       :field/translations {:en "Other locations"}
      }]
     [:company/problem-solution
      {:field/data-type          :data.type/ref-multi
       :field/default-ord        840
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/problem-solution
       :field/default-visibility {:editor :editor.type/multi-select
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Problem solutions"}
      }]
     [:company/market-challenges
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        850
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Market challenges"}}]
     [:company/market-opportunities
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        860
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Market opportunities"}
      }]
     [:company/need
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        870
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Need"}}]
     [:company/architecture
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        880
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Architecture"}}]
     [:company/main-actors
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        890
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Main actors"}}]
     [:company/incentives
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        900
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Incentives"}}]
     [:company/deterrents
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        910
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Deterrents"}}]
     [:company/concensus
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        915
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Consensus"}}]
     [:company/possible-attack-vectors
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        920
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Possible attack vectors"}}]
     [:company/applications
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        930
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Applications"}}]
     [:company/verdict-sections
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        940
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Verdict sections"}}]
     [:company/roadmap
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        950
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Roadmap"}}]
     [:company/next-development
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        960
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Next developments"}}]
     [:company/partners
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        970
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Partners"}}]
     [:company/tokenomics
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        980
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Tokenomics"}}]
     [:company/token-intrinsic-value
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        990
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Token intrinsic value"}}]
     [:company/questions
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        1000
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Questions"}}]
     [:company/strengths
      {:field/data-type             :data.type/text-multiline
       :field/default-ord           1010
       :field/constraints           {:constraint/string-max-length 2000}
       :field/default-visibility    {:editor :editor.type/textarea
                                     :grid   :editor.type/textarea
                                     :filter :filter.type/text
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations          {:en "Strengths"}}]
     [:company/risks
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        1020
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Risks"}}]
     [:company/industry-category
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        1030
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Industry category"}}]
     [:company/sales-pipeline
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        1040
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Sales pipeline"}
      }]
     [:company/marketing-plan
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        1050
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Marketing plan"}
      }]
     [:company/technical-due-diligence
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        1060
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Technical due diligence"}
      }]
     [:company/company-due-diligence
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        1070
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Company due diligence"}
      }]
     [:company/eis-due-diligence
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        1080
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "EIS due diligence"}
      }]
     [:company/financials-model-analysis
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        1090
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Financials model analysis"}
      }]
     [:company/customer-problem-solutions
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        1100
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Solutions to customer problems"}
      }]

     [:company/sector
      {:field/data-type          :data.type/ref-multi
       :field/default-ord        1110
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/sector
       :field/default-visibility {:editor :editor.type/multi-select
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Sector"}
      }]
     [:company/cluster
      {:field/data-type          :data.type/ref-multi
       :field/default-ord        1120
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/cluster
       :field/default-visibility {:editor :editor.type/multi-select
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Cluster"}
      }]
     [:company/sector-broad
      {:field/data-type          :data.type/ref-multi
       :field/default-ord        1130
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/sector-broad
       :field/default-visibility {:editor :editor.type/multi-select
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Broad sector"}
      }]
     [:company/sector-sub
      {:field/data-type          :data.type/ref-multi
       :field/default-ord        1140
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/sector-sub
       :field/default-visibility {:editor :editor.type/multi-select
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Sub sector"}
      }]
     [:company/hkstp-office-location
      {:field/data-type          :data.type/ref-single
       :field/default-ord        1150
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/hkstp-office-location
       :field/default-visibility {:field-visibility :not-in-use-blocked
                                  :editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "HKSTP office location"}
      }]
     [:company/proposed-round-participants
      {:field/data-type          :data.type/ref-multi
       :field/default-ord        1150
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/proposed-round-participants
       :field/default-visibility {:editor :editor.type/multi-select
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Round participants"}
      }]
     [:company/preferred-funding-structure
      {:field/data-type          :data.type/ref-multi
       :field/default-ord        1150
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/preferred-funding-structure
       :field/default-visibility {:editor :editor.type/multi-select
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Preferred funding structure"}
      }]

     [:company/hkai-lab
      {:field/data-type          :data.type/ref-single
       :field/default-ord        1160
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/hkai-lab
       :field/default-visibility {:field-visibility :not-in-use-blocked
                                  :editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "HKAI lab"}
      }]
     [:company/global-acceleration-academy
      {:field/data-type          :data.type/ref-single
       :field/default-ord        1160
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/global-acceleration-academy
       :field/default-visibility {:field-visibility :not-in-use-blocked
                                  :editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                 }
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Global Acceleration Academy"}
      }]
     [:company/category
      {:field/data-type          :data.type/ref-multi
       :field/default-ord        1200
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/category
       :field/default-visibility {:editor :editor.type/multi-select
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Category"}
      }]
     [:company/revenue-range-ltm
      {:field/data-type          :data.type/ref-single
       :field/default-ord        1150
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/revenue-range-ltm
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Revenue range LTM"}
      }]

     [:company/product-development-stage
      {:field/data-type          :data.type/ref-single
       :field/default-ord        1160
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/product-development-stage
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Product development stage"}
      }]
     [:company/lifecycle
      {:field/data-type          :data.type/ref-single
       :field/default-ord        1170
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/lifecycle
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Lifecycle"}
      }]
     [:company/TOL-A
      {:field/data-type :data.type/keyword
       :field/default-ord 1170
       :field/not-importable? true
       :value-type :keyword
       :options tol-a/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "TOL-A"}
      }]
     [:company/TOL-B
      {:field/data-type :data.type/keyword
       :field/default-ord 1170
       :field/not-importable? true
       :value-type :keyword
       :options tol-b/options
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "TOL-B"}
      }]
     [:company/tag-head-count
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Head count"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        200
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-head-count
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-ownership
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Ownership"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        210
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-ownership
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-type
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Company Type"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        220
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-type
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-maturity
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Maturity"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        230
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-maturity
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-technology-maturity
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Technology maturity"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        230
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-technology-maturity
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-stage
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Stage"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        240
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-stage
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :in-use}
       :value-type               :entity}]
     [:company/tag-round
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Round"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        240
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-round
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-credit-rating
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Credit rating"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        250
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-credit-rating
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-potential-buyers
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Potential buyers"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        260
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-potential-buyers
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-potential-acquisition-targets
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Potential aquisition targets"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        270
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-potential-acquisition-targets
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-fit
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Fit"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        280
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-fit
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-sub-sector
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Sub-sector"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        290
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-sub-sector
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-industry-type
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Industry type"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        300
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-industry-type
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :in-use}
       :value-type               :entity}]
     [:company/tag-impact-metrics
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Impact metrics"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        300
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-impact-metrics
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-sector
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Sector"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        310
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-sector
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :in-use}
       :value-type               :entity}]
     [:company/tag-business-model
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Business model"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        320
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-business-model
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-category
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Category"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        330
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-category
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :in-use}
       :value-type               :entity}]
     [:company/tag-sales-model
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Sales model"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        340
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-sales-model
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-industry
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Industry"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        350
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-industry
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :value-type               :entity}]
     [:company/tag-risks
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Risks"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        360
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-risks
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-size-category
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Size category"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        370
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-size-category
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-team-type
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Team type"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        380
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-team-type
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :in-use}
       :value-type               :entity}]
     [:company/tag-strategy
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Strategy"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        390
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-strategy
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-group
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Group"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        400
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-group
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-value-chain
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Value chain"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        410
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-value-chain
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-investor-type
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Investor type"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        420
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-investor-type
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-rating
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Rating"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        430
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-rating
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-strategic-fit
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Strategic fit"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        440
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-strategic-fit
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :in-use}
       :value-type               :entity}]
     [:company/tag-m-n-a-potential
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "M&A potential"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        450
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-m-n-a-potential
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-target-markets
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Target markets"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        460
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-target-markets
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-ipr
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "IPR"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        470
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-ipr
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-technology
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Technology"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        480
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-technology
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-business-vertical-segment
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Business Vertical (Segment)"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        490
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-business-vertical-segment
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-business-unit-function
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Business Unit/Function"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        500
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-business-unit-function
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]

     [:company/one-line-description
      {:field/data-type          :data.type/text-simple
       :field/default-ord        1270
       :field/default-visibility {:editor :editor.type/text-simple
                                  :grid   :editor.type/text-simple
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "One line description"}}]

     [:company/tag-product-category
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Product category"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        1280
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-product-category
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]

     [:company/tag-membership-category
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Membership category"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        1290
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-membership-category
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]

     [:company/datetime-preferred-starting-date
      {:field/data-type          :data.type/datetime
       :field/default-ord        1300
       :field/default-visibility {:editor :editor.type/date
                                  :grid   :editor.type/date
                                  :filter :filter.type/date
                                  :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Preferred starting date"}}]

     [:company/projected-ftes-moving-in
      {:field/data-type             :data.type/number
       :field/default-ord           1310
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:field-visibility :not-in-use-blocked
                                     :editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/custom-params         (fn [_]
                                      {:base-multiplier "1"})
       :field/translations          {:en "Number of people moving in"}}]
     [:company/cur-tesi-bang-for-buck
      {:field/data-type             :data.type/currency-value
       :field/default-ord           1311
       :field/secondary-attribute   :company/cur-proposed-funding-from-us--sym
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/system}
       :field/composite-attributes  [:company/number-of-employees-in-Finland
                                     :company/cur-proposed-funding-from-us]
       :field/calculated-field      (fn [{:keys [logger entity-data entity-lookup tr bigdec->float
                                                 get-field-schema display-string]}]
                                      (let [i-personnel-in-finland
                                            (:company/number-of-employees-in-Finland entity-data)
                                            f-our-funding (bigdec->float
                                                           (:company/cur-proposed-funding-from-us
                                                            entity-data))]
                                        (cond
                                          (not (number? i-personnel-in-finland))
                                          (tr :not-a-number
                                              [(tr :company/number-of-employees-in-Finland)])
                                          (not (number? f-our-funding))
                                          (tr :not-a-number
                                              [(tr :company/cur-proposed-funding-from-us)])
                                          :else
                                          (let [dividend f-our-funding
                                                divider i-personnel-in-finland]
                                            (if (zero? divider)
                                              (if (> dividend 0)
                                                "+∞"  ;; // NA
                                                "-∞") ;; // NA
                                              (/ dividend divider))))))
       :field/custom-params         (fn [_]
                                      {:base-multiplier "1"})
       :field/translations          {:en "Tesi bang for the buck"}}]
     [:company/cur-tesi-bang-for-buck--sym
      {:field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations {:en "Tesi bang for the buck (symbol)"}
       :field/data-type :data.type/currency-symbol
       :field/secondary-field? true
       :field/not-clearable? true
       :field/related-fields :company/cur-tesi-bang-for-buck
       :options currencies/options
       :field/composite-attributes [:company/cur-proposed-funding-from-us--sym]
       :field/calculated-field (fn [{:keys [logger entity-data entity-lookup tr bigdec->float
                                            get-field-schema display-string]}]
                                 (let [k-our-funding-symbol
                                       (:company/cur-proposed-funding-from-us--sym entity-data)]
                                   k-our-funding-symbol))
       :field/default-visibility {:editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use-blocked}
       :value-type :keyword}]

     [:company/tesi-runway
      {:field/data-type             :data.type/number
       :field/default-ord           1310
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/system}
       :field/composite-attributes  [:company/cur-current-cash-position-inc-bank-receivables
                                     :company/cur-fixed-costs-current-last-month]
       :field/calculated-field
       (fn [{:keys [logger entity-data entity-lookup tr bigdec->float get-field-schema
                    display-string]}]
         (let [f-fixed-month (bigdec->float (:company/cur-fixed-costs-current-last-month
                                             entity-data))
               f-cash (bigdec->float (:company/cur-current-cash-position-inc-bank-receivables
                                      entity-data))]
           (cond
             (not (number? f-fixed-month))
             (tr :not-a-number [(tr :company/cur-fixed-costs-current-last-month)])
             (not (number? f-cash))
             (tr :not-a-number
                 [(tr :company/cur-current-cash-position-inc-bank-receivables)])
             (not (= (:company/cur-current-cash-position-inc-bank-receivables--sym entity-data)
                     (:company/cur-fixed-costs-current-last-month--sym entity-data)))
             (tr :not-same-currency
                 [(str (tr :company/cur-current-cash-position-inc-bank-receivables)
                       ", "
                       (tr :company/cur-fixed-costs-current-last-month))])
             :else
             (let [dividend f-cash
                   divider f-fixed-month]
               (if (zero? divider)
                 (if (> dividend 0)
                   "+∞"
                   "-∞")
                 (/ dividend divider))))))
       :field/custom-params         (fn [_]
                                      {:base-multiplier "1"})
       :field/translations          {:en "Our view on runway"}}]
     [:company/tesi-equity-ratio
      {:field/data-type             :data.type/percentage
       :field/default-ord           1310
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/percentage
                                     :grid   :editor.type/percentage
                                     :filter :filter.type/percentage
                                     :field-visibility :not-in-use-blocked}
       :field/composite-attributes  [:company/cur-equity
                                     :company/cur-long-term-advances
                                     :company/cur-balance-sheet-size]
       :field/calculated-field
       (fn [{:keys [logger entity-data entity-lookup tr bigdec->float get-field-schema
                    display-string]}]
         (let [f-equity (bigdec->float (:company/cur-equity entity-data))
               f-long-advances (bigdec->float (:company/cur-long-term-advances entity-data))
               f-balance-sheet (bigdec->float (:company/cur-balance-sheet-size entity-data))]
           (cond
             (not (number? f-equity))
             (tr :not-a-number [(tr :company/cur-equity)])
             (not (number? f-long-advances))
             (tr :not-a-number [(tr :company/cur-long-term-advances)])
             (not (number? f-balance-sheet))
             (tr :not-a-number [(tr :company/cur-balance-sheet-size)])
             (not (= (:company/cur-equity--sym entity-data)
                     (:company/cur-long-term-advances--sym entity-data)
                     (:company/cur-balance-sheet-size--sym entity-data)))
             (tr :not-same-currency
                 [(str (tr :company/cur-equity)
                       ", "
                       (tr :company/cur-long-term-advances)
                       ", "
                       (tr :company/cur-balance-sheet-size))])
             :else
             (let [dividend f-equity
                   divider (- f-balance-sheet f-long-advances)]
               (if (= 0.0 (float divider))
                 (if (> dividend 0)
                   "+∞"
                   "-∞")
                 (/ dividend divider))))))

       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/system}
       :field/custom-params         (fn [_]
                                      {:base-multiplier "1"})
       :field/translations          {:en "Equity ratio"}}]
     [:company/quick-ratio
      {:field/data-type             :data.type/number
       :field/default-ord           1310
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use-blocked}
       :field/composite-attributes  [:company/cur-cash-and-bank-receivables-at-end-of-last-year
                                     :company/cur-short-term-receivables
                                     :company/cur-short-term-debt
                                     :company/cur-short-term-advances]
       :field/calculated-field
       (fn [{:keys [logger entity-data entity-lookup tr bigdec->float get-field-schema
                    display-string]}]
         (let [f-bank (bigdec->float (:company/cur-cash-and-bank-receivables-at-end-of-last-year
                                      entity-data))
               f-short-receivables (bigdec->float (:company/cur-short-term-receivables entity-data))
               f-short-debt (bigdec->float (:company/cur-short-term-debt entity-data))
               f-short-adv (bigdec->float (:company/cur-short-term-advances entity-data))]
           (cond
             (not (number? f-bank))
             (tr :not-a-number
                 [(tr :company/cur-cash-and-bank-receivables-at-end-of-last-year)])
             (not (number? f-short-receivables))
             (tr :not-a-number [(tr :company/cur-short-term-receivables)])
             (not (number? f-short-debt))
             (tr :not-a-number [(tr :company/cur-short-term-debt)])
             (not (number? f-short-adv))
             (tr :not-a-number [(tr :company/cur-short-term-advances)])
             (not (= (:company/cur-cash-and-bank-receivables-at-end-of-last-year--sym entity-data)
                     (:company/cur-short-term-receivables--sym entity-data)
                     (:company/cur-short-term-debt--sym entity-data)
                     (:company/cur-short-term-advances--sym entity-data)))
             (tr :not-same-currency
                 [(str (tr :company/cur-cash-and-bank-receivables-at-end-of-last-year)
                       ", "
                       (tr :company/cur-short-term-receivables)
                       ", "
                       (tr :company/cur-short-term-debt)
                       ", "
                       (tr :company/cur-short-term-advances))])
             :else
             (let [dividend (+ f-bank f-short-receivables)
                   divider (- f-short-debt f-short-adv)]
               (if (= 0.0 (float divider))
                 (if (> dividend 0)
                   "+∞"
                   "-∞")
                 (/ dividend divider))))))

       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/system}
       :field/custom-params         (fn [_]
                                      {:base-multiplier "1"})
       :field/translations          {:en "Quick ratio"}}]
     [:company/tesi-net-working-capital-percentage
      {:field/data-type             :data.type/percentage
       :field/default-ord           1310
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/percentage
                                     :grid   :editor.type/percentage
                                     :filter :filter.type/percentage
                                     :field-visibility :not-in-use-blocked
                                    }
       :field/composite-attributes  [:company/cur-accounts-receivable
                                     :company/cur-accounts-payable
                                     :company/cur-current-assets
                                     :company/cur-tesi-revenue-2019]
       :field/calculated-field
       (fn [{:keys [logger entity-data entity-lookup tr bigdec->float get-field-schema
                    display-string]}]
         (let [f-receivables (bigdec->float (:company/cur-accounts-receivable entity-data))
               f-payable (bigdec->float (:company/cur-accounts-payable entity-data))
               f-assets (bigdec->float (:company/cur-current-assets entity-data))
               f-revenue (bigdec->float (:company/cur-tesi-revenue-2019 entity-data))]
           (cond
             (not (number? f-receivables))
             (tr :not-a-number [(tr :company/cur-accounts-receivable)])
             (not (number? f-payable))
             (tr :not-a-number [(tr :company/cur-accounts-payable)])
             (not (number? f-assets))
             (tr :not-a-number [(tr :company/cur-current-assets)])
             (not (number? f-revenue))
             (tr :not-a-number [(tr :company/cur-tesi-revenue-2019)])
             (not (= (:company/cur-accounts-receivable--sym entity-data)
                     (:company/cur-accounts-payable--sym entity-data)
                     (:company/cur-current-assets--sym entity-data)
                  ))
             (tr :not-same-currency
                 [(str (tr :company/cur-accounts-receivable)
                       ", "
                       (tr :company/cur-accounts-payable)
                       ", "
                       (tr :company/cur-current-assets)
                  )])
             :else
             (let [dividend (- (+ f-assets f-receivables)
                               f-payable)
                   divider f-revenue]
               (if (= 0.0 (float divider))
                 (if (> dividend 0)
                   "+∞"
                   "-∞")
                 (/ dividend divider))))))
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/system}
       :field/custom-params         (fn [_]
                                      {:base-multiplier "1"})
       :field/translations          {:en "Net working capital (%)"}}]
     [:company/tesi-ebitda-percentage
      {:field/data-type             :data.type/percentage
       :field/default-ord           1310
       :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/percentage
                                     :grid   :editor.type/percentage
                                     :filter :filter.type/percentage
                                     :field-visibility :not-in-use-blocked
                                    }
       :field/composite-attributes  [:company/cur-tesi-ebitda-2019
                                     :company/cur-tesi-revenue-2019]
       :field/calculated-field      (fn [{:keys [logger entity-data entity-lookup tr bigdec->float
                                                 get-field-schema display-string]}]
                                      (let [f-ebitda (bigdec->float (:company/cur-tesi-ebitda-2019
                                                                     entity-data))
                                            f-revenue (bigdec->float (:company/cur-tesi-revenue-2019
                                                                      entity-data))]
                                        (cond
                                          (not (number? f-ebitda))
                                          (tr :not-a-number [(tr :company/cur-tesi-ebitda-2019)])
                                          (not (number? f-revenue))
                                          (tr :not-a-number
                                              [(tr :company/cur-tesi-revenue-2019)])
                                          (not (= (:company/cur-tesi-ebitda-2019--sym entity-data)
                                                  (:company/cur-tesi-revenue-2019--sym
                                                   entity-data)))
                                          (tr :not-same-currency
                                              [(str (tr :company/cur-tesi-ebitda-2019)
                                                    ", "
                                                    (tr :company/cur-tesi-revenue-2019))])
                                          :else
                                          (let [dividend f-ebitda
                                                divider f-revenue]
                                            (if (= 0.0 (float divider))
                                              (if (> dividend 0)
                                                "+∞"
                                                "-∞")
                                              (/ dividend divider))))))
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/system}
       :field/custom-params         (fn [_]
                                      {:base-multiplier "1"})
       :field/translations          {:en "EBITDA-%"}}]
     [:company/company-view-on-runway-in-months
      {:field/data-type             :data.type/number
       :field/default-ord           1310
       :field/constraints           {:constraint/number-positive        true
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    {:editor :editor.type/number
                                     :grid   :editor.type/number
                                     :filter :filter.type/number
                                     :field-visibility :not-in-use}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/custom-params         (fn [_]
                                      {:base-multiplier "1"})
       :field/translations          {:en "Company view on runway (months)"}}]

     [:company/scalable-business
      {:field/data-type          :data.type/ref-single
       :field/default-ord        1330
       :value-type               :entity
       :create-on-fly?           true
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/scalable-business
       :field/default-visibility {:field-visibility :not-in-use
                                  :editor :editor.type/select
                                  :grid   :editor.type/select
                                  :filter :filter.type/select}
       :field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Is the business scalable?"}
      }]
     [:company/why-part-of-maria-01
      {:field/data-type          :data.type/text-multiline
       :field/default-ord        255
       :field/default-visibility {:editor :editor.type/textarea
                                  :grid   :editor.type/textarea
                                  :filter :filter.type/text
                                  :field-visibility :not-in-use-blocked}
       :field/default-authorization {:access/read  :role/guest-sell-side
                                     :access/write :role/guest-sell-side}
       :field/translations       {:en "Why do you want to be part of Maria 01?"}
      }]
     [:company/tag-crop
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Crop"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        1300
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-crop
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-growth-management
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Growth management"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        1303
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-growth-management
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-crop-care-management
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Crop care management"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        1304
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-crop-care-management
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-yield-and-harvest-management
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Yield & harvest management"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        1305
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-yield-and-harvest-management
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-nutrient-and-water-management
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Nutrient & water management"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        1306
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-nutrient-and-water-management
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-on-farm-processing-and-consumption
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "On-farm processing/consumption"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        1307
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-on-farm-processing-and-consumption
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-farm-management-system
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Farm Management System"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        1308
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-farm-management-system
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]

     [:company/tag-application
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Application"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        1310
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-application
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]]
    (su/currency-field :company/cur-debt-in-bs-last-known
                       "Debt in BS (last known)"
                       {:default-field-visibility :not-in-use
                        :authorization su/access-ssg-ssg})
    (su/currency-field :company/cur-equity-in-bs-last-known
                       "Equity in BS (last known)"
                       {:default-field-visibility :not-in-use
                        :authorization su/access-ssg-ssg})
    (su/currency-field :company/cur-net-income-last-known
                       "Net income (last known)"
                       {:default-field-visibility :not-in-use
                        :authorization su/access-ssg-ssg})
    (su/currency-field :company/cur-net-interest-income
                       "Interest income"
                       {:default-field-visibility :not-in-use
                        :authorization su/access-ssg-ssg})
    (su/currency-field :company/cur-total-deposits
                       "Total deposits"
                       {:default-field-visibility :not-in-use
                        :authorization su/access-ssg-ssg})
    (su/currency-field :company/cur-total-loans
                       "Total loans"
                       {:default-field-visibility :not-in-use
                        :authorization su/access-ssg-ssg})
    (su/keyword-field :company/legal-domicile
                      "Legal domicile"
                      country/options
                      {:default-field-visibility :not-in-use
                       :authorization su/access-ssg-ssg})
    (su/keyword-field :company/operational-hq
                      "Operational HQ"
                      country/options
                      {:default-field-visibility :not-in-use
                       :authorization su/access-ssg-ssg})
    (su/keyword-multi-field :company/operational-presence
                            "Operational presence"
                            country/options
                            {:default-field-visibility :not-in-use
                             :authorization su/access-ssg-ssg})
    (su/percentage-field :company/cost-to-income-ratio
                         "Cost to income ratio"
                         {:default-field-visibility :not-in-use
                          :authorization su/access-ssg-ssg})
    (su/percentage-field :company/ebitda-margin-last-known
                         "Ebitda margin (last known)"
                         {:default-field-visibility :not-in-use
                          :authorization su/access-ssg-ssg})
    (su/percentage-field :company/nim
                         "NIM"
                         {:default-field-visibility :not-in-use
                          :authorization su/access-ssg-ssg})
    (su/percentage-field :company/roa
                         "ROA"
                         {:default-field-visibility :not-in-use
                          :authorization su/access-ssg-ssg})
    (su/percentage-field :company/roe
                         "ROE"
                         {:default-field-visibility :not-in-use
                          :authorization su/access-ssg-ssg})
    (su/text-simple-field :company/provisioning
                          "Provisioning"
                          {:default-field-visibility :not-in-use
                           :authorization su/access-ssg-ssg})

    (su/text-simple-field :company/ticker
                          "Ticker"
                          {:default-field-visibility :not-in-use
                           :authorization su/access-ssg-ssg})
    [[:company/tag-disease-domain
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Disease domain"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        1312
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-disease-domain
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]
     [:company/tag-rare-disease
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "Rare disease"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        1313
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-rare-disease
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]

     [:company/tag-fda-designation
      {:field/default-authorization {:access/read   :role/guest-sell-side
                                     :access/create :role/admin
                                     :access/write  :role/guest-sell-side}
       :field/translations       {:en "FDA designation"}
       :field/data-type          :data.type/ref-multi
       :create-on-fly?           true
       :field/default-ord        1314
       :field/entity-types       [:entity.type/named-value]
       :named-value-attribute    :company/tag-fda-designation
       :field/default-visibility {:editor :editor.type/none
                                  :grid   :editor.type/multi-select
                                  :filter :filter.type/select
                                  :field-visibility :not-in-use}
       :value-type               :entity}]]
    (su/text-multiline-field :company/treatment-type-modality
                             "Treatment type/modality"
                             {:default-field-visibility :not-in-use
                              :authorization su/access-ssg-ssg})
    (su/text-multiline-field :company/small-molecules
                             "Small molecules"
                             {:default-field-visibility :not-in-use
                              :authorization su/access-ssg-ssg})
    (su/text-multiline-field :company/drug-repurposing
                             "Drug repurposing"
                             {:default-field-visibility :not-in-use
                              :authorization su/access-ssg-ssg})
    (su/text-multiline-field :company/mechanism-of-action
                             "Mechanism of action"
                             {:default-field-visibility :not-in-use
                              :authorization su/access-ssg-ssg})
    (su/text-simple-field :company/formulation
                          "Formulation"
                          {:default-field-visibility :not-in-use
                           :authorization su/access-ssg-ssg})
    (su/text-multiline-field :company/project-dosing-regime
                             "Project dosing regime"
                             {:default-field-visibility :not-in-use
                              :authorization su/access-ssg-ssg})
    (su/option-field-multi :company/academic-affiliations
                           "Academic affiliations"
                           {:default-field-visibility :not-in-use
                            :authorization su/access-ssg-ssg})
    (su/option-field-multi :company/antibodies
                           "Antibodies"
                           {:default-field-visibility :not-in-use
                            :authorization su/access-ssg-ssg})
    (su/option-field-multi :company/gene-and-cellular-therapies
                           "Gene and cellular therapies"
                           {:default-field-visibility :not-in-use
                            :authorization su/access-ssg-ssg})
    (su/option-field-multi :company/oligonucleotides
                           "Oligonucleotides"
                           {:default-field-visibility :not-in-use
                            :authorization su/access-ssg-ssg})
    (su/option-field-multi :company/viral-vectors
                           "Viral vectors"
                           {:default-field-visibility :not-in-use
                            :authorization su/access-ssg-ssg})
    (su/option-field-multi :company/drug-conjugates
                           "Drug conjugates"
                           {:default-field-visibility :not-in-use
                            :authorization su/access-ssg-ssg})
    (su/option-field-multi :company/oncologies
                           "Oncologies"
                           {:default-field-visibility :not-in-use
                            :authorization su/access-ssg-ssg})
    (su/text-multiline-field :company/expected-commercial-launch
                             "Expected commercial launch"
                             {:default-field-visibility :not-in-use
                              :authorization su/access-ssg-ssg})
    (su/option-field :company/fda-regulatory-pathway
                     "FDA regulatory pathway?"
                     {:default-field-visibility :not-in-use
                      :authorization su/access-ssg-ssg})
    (su/option-field :company/fda-review-status
                     "FDA review status"
                     {:default-field-visibility :not-in-use
                      :authorization su/access-ssg-ssg})
    (su/option-field :company/financing-stage
                     "Financing stage"
                     {:default-field-visibility :not-in-use
                      :authorization su/access-ssg-ssg})
    (su/text-multiline-field :company/impact-opportunity-and-mandate
                             "Impact opportunity & mandate"
                             {:default-field-visibility :not-in-use
                              :authorization su/access-ssg-ssg})
    (su/text-simple-field :company/ip-source
                          "IP source"
                          {:default-field-visibility :not-in-use
                           :authorization su/access-ssg-ssg})
    (su/option-field :company/lead-asset-development-stage
                     "Lead asset development stage"
                     {:default-field-visibility :not-in-use
                      :authorization su/access-ssg-ssg})
    (su/option-field-multi :company/tag-lead-indication
                           "Lead indication"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use})
    (su/currency-field :company/cur-non-dilutive-funding-to-date
                       "Non-dilutive funding to date"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use})
    (su/option-field :company/orphan-disease
                     "Orphan disease?"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/option-field-multi :company/tag-other-indications
                           "Other indications"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/past-research-achievements
                             "Past research achievements"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/option-field :company/path-to-human-trials
                     "Path to human trials?"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/pediatric-innovation-strategy
                             "Pediatric innovation strategy"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/option-field-multi :company/tag-pediatric-population-focus
                           "Pediatric population focus"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/pipeline-research
                             "Pipeline research"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use-blocked})
    (su/option-field-multi :company/tag-platform-technology
                           "Platform technology"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use})
    (su/option-field :company/rating-business-strategy
                     "Rating - Business strategy"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/option-field :company/rating-care-profile
                     "Rating - Care profile"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/option-field :company/rating-competition
                     "Rating - Competition"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/option-field :company/rating-esg
                     "Rating - ESG"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/option-field :company/rating-exit-potential
                     "Rating - Exit potential"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/option-field :company/rating-impact-alignment
                     "Rating - Impact alignment"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/option-field :company/rating-intangibles
                     "Rating - Intangibles"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/option-field :company/rating-ipr
                     "Rating - IPR"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/option-field :company/rating-market
                     "Rating - Market"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/option-field :company/rating-team
                     "Rating - Team"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/option-field :company/rating-technology
                     "Rating - Technology"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/regulatory-strategy
                             "Regulatory strategy"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/research-rating-and-summary
                             "Research rating & summary"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/treatment-alternatives
                             "Treatment alternatives"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/option-field-multi :company/tag-themes
                           "Themes"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use})
    (su/option-field-multi :company/tag-stage-of-financing
                           "Stage of financing"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use})
    (su/option-field-multi :company/tag-additional-pipeline-indications
                           "Additional pipeline indications"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/historic-research-achievements
                             "Historic research achievements"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/portfolio-pipeline-research
                             "Portfolio/pipeline research"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/projected-timing-to-commercial-launch
                             "Projected timing to commercial launch"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/option-field :company/platform-technology
                     "Platform technology"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/option-field-multi :company/tag-portfolios
                           "Portfolios"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/market-overview
                             "Market overview"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/risks-and-challenges
                             "Risks and challenges"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/option-field :company/attractiveness
                     "Attractiveness"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/percentage-field :company/equity-ratio
                         "Equity ratio"
                         {:authorization su/access-ssg-ssg
                          :default-field-visibility :not-in-use})
    (su/option-field-multi :company/office-locations
                           "Office locations"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use})
    (su/date-field :company/date-deadline-to-close
                   "Deadline to close"
                   {:authorization su/access-ssg-ssg
                    :default-field-visibility :not-in-use})
    (su/option-field :company/finders-fee-agreement
                     "Finder's fee agreement"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/option-field :company/company-presentation-sent
                     "Company presentation sent"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use})
    (su/text-simple-field :company/region
                          "Region"
                          {:authorization su/access-ssg-ssg
                           :default-field-visibility :not-in-use})
    (su/option-field-multi :company/tag-portfolio-sub-category
                           "Portfolio sub-category"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use})
    (su/currency-field :company/cur-revenue-2022
                       "Revenue 2022"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use})
    (su/currency-field :company/cur-revenue-2023
                       "Revenue 2023"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use})
    (su/currency-field :company/cur-revenue-2024
                       "Revenue 2024"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use})
    (su/currency-field :company/cur-revenue-2025
                       "Revenue 2025"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use})
    (su/currency-field :company/cur-revenue-2026
                       "Revenue 2026"
                       {:authorization su/access-ssg-ssg
                        :default-field-visibility :not-in-use})
    (su/numeric-field :company/rating-strategic-fit
                      "Rating - strategic fit"
                      {:authorization su/access-ssg-ssg
                       :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/strategic-fit
                             "Strategic fit"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/numeric-field :company/rating-technical-fit
                      "Rating - technical fit"
                      {:authorization su/access-ssg-ssg
                       :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/technical-fit
                             "Technical fit"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/numeric-field :company/numeric-rating-team
                      "Rating - team"
                      {:authorization su/access-ssg-ssg
                       :default-field-visibility :not-in-use})
    (su/numeric-field :company/rating-product-or-service
                      "Rating - product / service"
                      {:authorization su/access-ssg-ssg
                       :default-field-visibility :not-in-use})
    (su/numeric-field :company/numeric-rating-market
                      "Rating - market"
                      {:authorization su/access-ssg-ssg
                       :default-field-visibility :not-in-use})
    (su/numeric-field :company/rating-business-model
                      "Rating - business model"
                      {:authorization su/access-ssg-ssg
                       :default-field-visibility :not-in-use})
    (su/numeric-field :company/rating-business-overview
                      "Rating - business overview"
                      {:authorization su/access-ssg-ssg
                       :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/business-overview
                             "Business overview"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/option-field-multi :company/tag-deep-dive
                           "Deep dive"
                           {:authorization su/access-ssg-ssg
                            :default-field-visibility :not-in-use})
    (su/text-multiline-field :company/poc-comments
                             "POC comments"
                             {:authorization su/access-ssg-ssg
                              :default-field-visibility :not-in-use})
    (su/integer-field :company/number-of-us-patents
                      "Number of US patents"
                      {:authorization su/access-ssg-ssg
                       :default-field-visibility :not-in-use-blocked})
    (su/integer-field :company/number-of-international-patents-wipo-pct
                      "Number of international patents (WIPO/PCT)"
                      {:authorization su/access-ssg-ssg
                       :default-field-visibility :not-in-use-blocked})
    (su/option-field :company/predicate-device-predicate-type
                     "Predicate device: predicate type"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use-blocked})
    (su/text-simple-field :company/predicate-device-predicate-510-k-number
                          "Predicate device: predicate 510(k) number"
                          {:authorization su/access-ssg-ssg
                           :default-field-visibility :not-in-use-blocked})
    (su/option-field :company/substantial-equivalence-same-safety
                     "Substantial equivalence: same safety"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use-blocked})
    (su/option-field :company/substantial-equivalence-same-materials
                     "Substantial equivalence: same materials"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use-blocked})
    (su/option-field :company/substantial-equivalence-biocompatible
                     "Substantial equivalence: biocompatible"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use-blocked})
    (su/option-field :company/substantial-equivalence-same-mechanical-performance
                     "Substantial equivalence: same mechanical performance"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use-blocked})
    (su/option-field :company/substantial-equivalence-supporting-performance-data
                     "Substantial equivalence: supporting performance data"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use-blocked})
    (su/option-field :company/bridging-510-k-approvals-up-to-date
                     "Bridging: 510(k) approvals up to date"
                     {:authorization su/access-ssg-ssg
                      :default-field-visibility :not-in-use-blocked})
    (su/date-field :company/founding-date
                   "Founding Date"
                   {:authorization su/access-ssg-ssg
                    :default-field-visibility :not-in-use})
    (su/currency-field :company/cur-valuation-in-millions
                       "Valuation (in millions)"
                       {:authorization      su/access-ssg-ssg
                        :default-visibility :not-in-use})
    (su/text-multiline-field :company/sector-details
                             "Sector Details"
                             {:authorization      su/access-ssg-ssg
                              :default-visibility :not-in-use})

    (su/option-field :company/partner "Partner" {:default-visibility :not-in-use})
    (su/text-multiline-field :company/capital-raised-details
                             "Capital Raised Details"
                             {:default-visibility :not-in-use})
    (su/option-field-multi :company/race "Race" {:default-visibility :not-in-use-blocked})
    (su/option-field-multi :company/country-of-origin
                           "Country of Origin"
                           {:default-visibility :not-in-use-blocked})
    (su/option-field-multi :company/gender "Gender" {:default-visibility :not-in-use-blocked})
    (su/option-field-multi :company/lgbtq "LGBTQ+" {:default-visibility :not-in-use-blocked})
    (su/datetime-field :company/log-date "Log Date" {:default-visibility :not-in-use-blocked})
    (su/text-multiline-field :company/source-details
                             "Source Details"
                             {:default-visibility :not-in-use})
    (su/option-field :company/fundraising-stage
                     "Fundraising Stage"
                     {:default-visibility :not-in-use})
    (su/multi-ref-field :company/risk-entities
                        "Risks"
                        [:entity.type/risk-holding]
                        {:authorization    su/access-user-user
                         :editor-type      :editor.type/none
                         :grid-type        :editor.type/none
                         :filter-type      :filter.type/boolean
                         :required-modules [:module/risk-module]})

    (su/option-field-multi :company/rule-of-40 "Rule of 40" {})
    (su/option-field-multi :company/gross-retention "Gross Retention" {})
    (su/option-field-multi :company/net-retention "Net Retention" {})
    (su/option-field-multi :company/recurring-percentage-slot "% Recurring" {})
    (su/option-field-multi :company/functional-gross-margins "Functional Gross Margins" {})
    (su/option-field-multi :company/arr-customer-concentration "ARR Cust. Concentration" {})

   )))
